import React, { useEffect, useState } from "react";
import { Dialog } from "@material-ui/core";
import { PriceDetail } from "../../../../../../models/Order";
import { RefundCancelledOrderByAdmin } from "../../../../../../services/OrderService";

const METHOD_LABEL: Record<number, string> = { 1: "OnePay", 2: "PayPal", 3: "Stripe" };

/** Refund a booking that is already "Cancelled · No refund" (Quân 16/10/2026). */
export default function RefundCancelledDialog(props: {
	open: boolean;
	order: PriceDetail;
	onClose: () => void;
	onDone: (message: string) => void;
}) {
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		if (props.open) {
			setBusy(false);
			setError("");
		}
	}, [props.open]);

	const o = props.order;
	const amount = (Math.round((o.totalMoney || 0) * 100) / 100).toFixed(2);
	const method = METHOD_LABEL[Number(o.paymentMethod)] || "—";
	const customer = (o as any).customer?.email || (o as any).user?.email || o.email || "—";

	const submit = async () => {
		if (busy || !o.id) return;
		setBusy(true);
		setError("");
		try {
			const r = await RefundCancelledOrderByAdmin(o.id);
			props.onDone(
				r?.refundStatus === 2
					? `Refund of ${amount} USD for booking #${o.id} is pending at ${method}.`
					: `${amount} USD refunded to the customer for booking #${o.id} via ${method}.`
			);
		} catch (err: any) {
			setError(err?.response?.data?.message || err?.message || "Refund failed — nothing was changed.");
			setBusy(false);
		}
	};

	return (
		<Dialog
			open={props.open}
			onClose={busy ? undefined : props.onClose}
			maxWidth="xs"
			fullWidth>
			<div style={{ padding: 24, fontSize: 14, color: "#111827" }}>
				<div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Refund booking #{o.id}</div>
				<div style={{ color: "#6B7280", marginBottom: 16 }}>
					This booking was cancelled without a refund. Refund the customer now?
				</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "100px 1fr",
						rowGap: 6,
						padding: 12,
						borderRadius: 10,
						background: "#F9FAFB",
						marginBottom: 14,
					}}>
					<span style={{ color: "#6B7280" }}>Customer</span>
					<span style={{ wordBreak: "break-all" }}>{customer}</span>
					<span style={{ color: "#6B7280" }}>Refund</span>
					<span style={{ fontWeight: 700 }}>
						{amount} USD · {method}
					</span>
				</div>
				<div style={{ color: "#4B5563", marginBottom: 6 }}>
					Full refund to the original payment method. The customer gets the “Booking Canceled” email with the
					refunded amount. The booking stays <b>Cancelled</b>; the label changes from <b>No refund</b> to{" "}
					<b>Refunded</b>. Station sees no change.
				</div>
				{error && (
					<div
						role="alert"
						style={{
							background: "#FEF2F2",
							color: "#B91C1C",
							borderRadius: 10,
							padding: "10px 12px",
							margin: "12px 0 0",
							lineHeight: 1.45,
						}}>
						{error}
					</div>
				)}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
					<button
						type="button"
						onClick={props.onClose}
						disabled={busy}
						style={{
							padding: "10px 16px",
							borderRadius: 8,
							border: "1px solid #D1D5DB",
							background: "#fff",
							cursor: busy ? "not-allowed" : "pointer",
							fontWeight: 600,
						}}>
						Close
					</button>
					<button
						type="button"
						onClick={submit}
						disabled={busy}
						style={{
							padding: "10px 16px",
							borderRadius: 8,
							border: "none",
							fontWeight: 700,
							cursor: busy ? "not-allowed" : "pointer",
							color: "#fff",
							background: busy ? "#9CA3AF" : "#DC2626",
						}}>
						{busy ? "Working…" : `Refund ${amount} USD`}
					</button>
				</div>
			</div>
		</Dialog>
	);
}
